"use client";

import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Phone, Mail, Globe, MapPin, Building2, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhotoGallery } from "@/components/services/photo-gallery";
import { DailyUpdatesFeed } from "@/components/services/daily-updates-feed";
import { hasNewContent } from "@/app/actions/tradesman-updates";
import { trackTradesmanAnalytics } from "@/app/actions/tradesman-analytics";
import { getTradesmanRatingSummary } from "@/app/actions/tradesman-reviews";
import { RatingDisplay } from "@/components/tradesman/rating-display";

interface TradesmanAd {
  id: string;
  businessName: string;
  logo?: string | null;
  phone: string;
  email: string;
  tagline?: string | null;
  servicesDescription?: string | null;
  serviceArea?: string | null;
  website?: string | null;
  city?: string | null;
  state?: string | null;
  planType: string;
  photoGallery?: string[];
  category: {
    displayName: string;
    icon?: string | null;
  };
  _avgRating?: number;
  _reviewCount?: number;
}

interface BusinessCardProps {
  ad: TradesmanAd;
  className?: string;
}

export function BusinessCard({ ad, className }: BusinessCardProps) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [hasNew, setHasNew] = useState(false);
  const [ratingSummary, setRatingSummary] = useState<{ averageRating: number; totalReviews: number } | null>(null);
  const hasTrackedView = useRef(false);

  const location = ad.city && ad.state ? `${ad.city}, ${ad.state}` : ad.serviceArea;
  const isFeatured = ad.planType === "FEATURED" || ad.planType === "PREMIUM";

  // Load rating summary
  useEffect(() => {
    async function loadRating() {
      const result = await getTradesmanRatingSummary(ad.id);
      if (result.success && result.data) {
        setRatingSummary({
          averageRating: result.data.averageRating,
          totalReviews: result.data.totalReviews
        });
      }
    }
    loadRating();
  }, [ad.id]);

  // Check for new content
  useEffect(() => {
    async function checkNewContent() {
      const result = await hasNewContent(ad.id);
      setHasNew(result);
    }
    checkNewContent();
  }, [ad.id]);

  // Track view when card becomes visible (Intersection Observer)
  useEffect(() => {
    if (hasTrackedView.current) return;

    const observer = new IntersectionObserver(
      async (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !hasTrackedView.current) {
            hasTrackedView.current = true;
            await trackTradesmanAnalytics({
              tradesmanAdId: ad.id,
              eventType: "VIEW",
            });
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 }
    );

    const cardElement = document.querySelector(`[data-ad-id="${ad.id}"]`);
    if (cardElement) {
      observer.observe(cardElement);
    }

    return () => observer.disconnect();
  }, [ad.id]);

  // Track click events
  const trackClick = async (eventType: "PHONE_CLICK" | "EMAIL_CLICK" | "WEBSITE_CLICK" | "CLICK") => {
    await trackTradesmanAnalytics({
      tradesmanAdId: ad.id,
      eventType,
    });
  };

  return (
    <div
      className={cn(
        "business-card-container perspective-1000",
        className
      )}
      data-ad-id={ad.id}
      onClick={() => {
        setIsFlipped(!isFlipped);
        trackClick("CLICK");
      }}
    >
      <div
        className={cn(
          "business-card relative w-full min-h-[280px] transition-transform duration-700 transform-style-3d cursor-pointer",
          isFlipped && "rotate-y-180"
        )}
      >
        {/* Front of Card */}
        <div className="business-card-face absolute inset-0 backface-hidden">
          <div
            className={cn(
              "h-full rounded-xl shadow-lg hover:shadow-xl border-2 p-6 flex flex-col transition-all",
              "bg-card border-slate-200",
              isFeatured && "border-amber-500 shadow-amber-200 bg-gradient-to-br from-amber-50 to-white"
            )}
          >
            {/* Logo or Icon */}
            <div className="flex items-start justify-between mb-4">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 border-2 border-slate-200">
                {ad.logo ? (
                  <Image
                    width={256}
                    height={256}
                    src={ad.logo}
                    alt={ad.businessName}
                    className="w-full h-full object-cover rounded-full"
                  />
                ) : (
                  <Building2 className="w-8 h-8 text-slate-600" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                {isFeatured && (
                  <Badge variant="default" className="bg-gradient-to-r from-amber-500 to-amber-600 text-white text-xs font-semibold shadow-md">
                    ⭐ Featured
                  </Badge>
                )}
                {hasNew && (
                  <Badge className="bg-green-500 hover:bg-green-600 text-white text-xs font-semibold shadow-md">
                    <Sparkles className="h-3 w-3 mr-1" />
                    New
                  </Badge>
                )}
              </div>
            </div>

            {/* Business Name */}
            <h3 className="font-serif text-xl font-bold text-slate-900 mb-2 leading-tight">
              {ad.businessName}
            </h3>

            {/* Category Badge */}
            <Badge variant="secondary" className="w-fit mb-3 bg-emerald-100 text-emerald-800 border border-emerald-200 font-medium">
              {ad.category.displayName}
            </Badge>

            {/* Rating Display */}
            {ratingSummary && (
              <div className="mb-3">
                <RatingDisplay
                  rating={ratingSummary.averageRating}
                  reviewCount={ratingSummary.totalReviews}
                  size="sm"
                />
              </div>
            )}

            {/* Contact Info */}
            <div className="mt-auto space-y-2">
              <a
                href={`tel:${ad.phone}`}
                className="flex items-center gap-2 text-sm text-slate-700 hover:text-emerald-600 transition-colors font-medium"
                onClick={(e) => {
                  e.stopPropagation();
                  trackClick("PHONE_CLICK");
                }}
              >
                <Phone className="w-4 h-4" />
                <span>{ad.phone}</span>
              </a>
              <a
                href={`mailto:${ad.email}`}
                className="flex items-center gap-2 text-sm text-slate-600 hover:text-emerald-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  trackClick("EMAIL_CLICK");
                }}
              >
                <Mail className="w-4 h-4" />
                <span className="text-xs truncate">{ad.email}</span>
              </a>
            </div>

            {/* Flip Hint */}
            <div className="mt-3 text-center">
              <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold hover:text-emerald-700 transition-colors">
                Click to see more →
              </span>
            </div>
          </div>
        </div>

        {/* Back of Card */}
        <div className="business-card-face absolute inset-0 backface-hidden rotate-y-180">
          <div
            className={cn(
              "h-full rounded-xl shadow-lg border-2 p-6 overflow-y-auto",
              "bg-card border-slate-200",
              isFeatured && "border-amber-500 shadow-amber-200 bg-gradient-to-br from-amber-50 to-white"
            )}
          >
            <div className="space-y-4">
              {/* Business Name */}
              <h3 className="font-serif text-lg font-bold text-slate-900 border-b-2 border-slate-200 pb-2">
                {ad.businessName}
              </h3>

              {/* Tagline */}
              {ad.tagline && (
                <p className="text-sm italic text-slate-600 font-serif">
                  &quot;{ad.tagline}&quot;
                </p>
              )}

              {/* Services Description */}
              {ad.servicesDescription && (
                <div>
                  <h4 className="text-xs font-bold text-slate-900 mb-2 uppercase tracking-wider">
                    Services
                  </h4>
                  <p className="text-sm text-slate-700 leading-relaxed">
                    {ad.servicesDescription}
                  </p>
                </div>
              )}

              {/* Service Area */}
              {location && (
                <div className="flex items-start gap-2 text-sm text-slate-700 bg-slate-50 p-3 rounded-lg border border-slate-200">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-600" />
                  <span className="font-medium">{location}</span>
                </div>
              )}

              {/* Website */}
              {ad.website && (
                <a
                  href={ad.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-emerald-600 hover:text-emerald-700 transition-colors font-medium bg-emerald-50 p-3 rounded-lg border border-emerald-200 hover:bg-emerald-100"
                  onClick={(e) => {
                    e.stopPropagation();
                    trackClick("WEBSITE_CLICK");
                  }}
                >
                  <Globe className="w-4 h-4" />
                  <span>Visit Website</span>
                </a>
              )}

              {/* Photo Gallery */}
              {ad.photoGallery && ad.photoGallery.length > 0 && (
                <div onClick={(e) => e.stopPropagation()}>
                  <PhotoGallery photos={ad.photoGallery} />
                </div>
              )}

              {/* Daily Updates Feed */}
              <div onClick={(e) => e.stopPropagation()}>
                <DailyUpdatesFeed adId={ad.id} isOwner={false} />
              </div>

              {/* Back Hint */}
              <div className="mt-4 pt-3 border-t-2 border-slate-200 text-center">
                <span className="inline-flex items-center gap-1 text-xs text-emerald-600 font-semibold hover:text-emerald-700 transition-colors cursor-pointer">
                  ← Click to flip back
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
      `}</style>
    </div>
  );
}
